<template>
    <div class="combo js-select" :class="{ open }" ref="comboEl">
        <label :id="labelId" :for="comboId" class="control-label" :class="{ 'visible-hidden': hideSelectLabel }">
            {{ level1Label ? level1Label : 'Departments' }}
            <template v-if="byDepartmentLevel == 2"> & {{ level2Label ? level2Label : 'Schools' }} </template> Selection<span class="visible-hidden"
                >Required</span
            ><span class="important" aria-hidden="true">*</span>
        </label>
        <button
            v-tooltip="isDisabled ? disabledLabel : ''"
            :id="comboId"
            class="combo-input"
            :class="[...editClasses.addClassToButton]"
            role="combobox"
            :aria-controls="listboxId"
            :aria-expanded="open"
            :aria-labelledby="labelId"
            :aria-activedescendant="activeDescendantId"
            @click="isDisabled ? null : (open = !open)"
            @keydown="onKeyDown"
            :aria-disabled="isDisabled"
        >
            <span class="flex alignItemsCenter">
                <i
                    v-if="showDropdownIcon"
                    class="fas marginRight8"
                    :class="{ 'fa-dice-one': byDepartmentLevel == 1, 'fa-dice-two': byDepartmentLevel == 2 }"
                    aria-hidden="true"
                />

                <span class="paddingTop2">
                    <template v-if="!showSelection">
                        <template v-if="!isFiltering">Assign to</template>
                        {{ level1Label ? level1Label : 'Departments' }}
                        <template v-if="byDepartmentLevel == 2"> & {{ level2Label ? level2Label : 'Schools' }} </template>
                    </template>
                    <template v-else>
                        <template v-if="activeDepartment">
                            {{ activeDepartment.name }}
                            <template v-if="activeSchool && byDepartmentLevel == 2"> - {{ activeSchool.name }} </template>
                        </template>
                        <template v-else>
                            {{ level1Label ? level1Label : 'Departments' }}
                            <template v-if="byDepartmentLevel == 2"> & {{ level2Label ? level2Label : 'Schools' }} </template>
                        </template>
                    </template>
                </span>
            </span>

            <i
                class="marginLeft20"
                :class="{
                    'fa fa-caret-up': open,
                    'fa fa-caret-down': !open,
                }"
            />
        </button>
        <div :id="listboxId" class="combo-menu" :class="{ dropup: dropUp }" role="listbox" tabindex="-1">
            <ul role="listbox">
                <template v-for="(department, fIndex) in options" :key="fIndex">
                    <li
                        role="option"
                        :id="`${idBase}-${fIndex}`"
                        class="department-header combo-option"
                        :class="{ 'option-current': isHeaderActive(fIndex), ...(editClasses?.addClassToFirstList || {}) }"
                        @click="onOptionClick(department)"
                    >
                        {{ department.name }}
                    </li>

                    <li
                        v-if="byDepartmentLevel == 2"
                        v-for="(school, sIndex) in department.level2Departments"
                        :key="`${fIndex}-${sIndex}`"
                        :id="`${idBase}-${fIndex}-${sIndex}`"
                        role="option"
                        class="school-item combo-option"
                        :class="{ 'option-current': isSchoolActive(fIndex, sIndex) }"
                        @click="onOptionClick(department, school)"
                    >
                        {{ school.name }}
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
    options: {
        type: Array,
        required: true,
    },
    isDisabled: {
        type: Boolean,
        default: false,
    },
    dropUp: {
        type: Boolean,
        default: false,
    },
    editClasses: {
        type: Object,
        default: () => ({}),
    },
    showSelection: {
        type: Boolean,
        default: false,
    },
    level1Label: {
        type: String,
        default: null,
    },
    level2Label: {
        type: String,
        default: null,
    },
    hideSelectLabel: {
        type: Boolean,
        default: false,
    },
    showDropdownIcon: {
        type: Boolean,
        default: true,
    },
    isFiltering: {
        type: Boolean,
        default: false,
    },
    disabledLabel: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['changeDepartment']);

const route = useRoute();
const open = ref(false);
const activeIndex = ref(0);
const activeDepartment = ref(null);
const activeSchool = ref(null);
const idBase = ref('combo');
const comboEl = ref(null);

const labelId = computed(() => `${idBase.value}-label`);
const comboId = computed(() => idBase.value);
const listboxId = computed(() => `${idBase.value}-listbox`);
const activeDescendantId = computed(() => (open.value ? `${idBase.value}-${activeIndex.value}` : ''));

const byDepartmentLevel = computed(() => {
    const hasLevel2 = props.options.some((item) => Array.isArray(item.level2Departments) && item.level2Departments.length > 0);

    if (hasLevel2) {
        return 2;
    }

    return 1;
});

const onEmptySelect = () => {
    activeDepartment.value = null;
    activeSchool.value = null;
    emit('change', { department: null, school: null });
    open.value = false;
};

const updateActiveDepartment = (department, school = null) => {
    activeDepartment.value = department;
    activeSchool.value = school;
};

defineExpose({
    onEmptySelect,
    updateActiveDepartment,
});

const handleSelection = () => {
    if (!activeDepartment.value) return;

    emit('changeDepartment', {
        department: { id: activeDepartment.value.id, name: activeDepartment.value.name },
        school: activeSchool.value,
    });

    open.value = false;
};

const handleNavigation = (newIndex) => {
    let currentIndex = 0;

    for (let dIndex = 0; dIndex < props.options.length; dIndex++) {
        const department = props.options[dIndex];

        if (currentIndex === newIndex) {
            activeDepartment.value = department;
            activeSchool.value = null;
            activeIndex.value = currentIndex;
            scrollToActive(dIndex);
            return;
        }
        currentIndex++;

        if (department.level2Departments) {
            for (let sIndex = 0; sIndex < department.level2Departments.length; sIndex++) {
                const school = department.level2Departments[sIndex];

                if (currentIndex === newIndex) {
                    activeDepartment.value = department;
                    activeSchool.value = school;
                    activeIndex.value = currentIndex;
                    scrollToActive(`${dIndex}-${sIndex}`);
                    return;
                }
                currentIndex++;
            }
        }
    }
};

const onKeyDown = (event) => {
    if (props.isDisabled) return;

    if (event.key === 'Tab') {
        if (open.value) {
            open.value = false;
        }
        return;
    }

    const totalItems = props.options.reduce((total, department) => {
        const schoolCount = department.level2Departments?.length || 0;
        return total + 1 + schoolCount;
    }, 0);

    switch (event.key) {
        case 'Enter':
        case ' ':
            event.preventDefault();
            if (!open.value) {
                open.value = true;
                activeDepartment.value = props.options[0];
                activeSchool.value = null;
                activeIndex.value = 0;
            } else {
                handleSelection();
            }
            break;
        case 'ArrowDown':
            event.preventDefault();
            if (!open.value) {
                open.value = true;
                activeDepartment.value = props.options[0];
                activeSchool.value = null;
                activeIndex.value = 0;
            } else {
                handleNavigation(Math.min(activeIndex.value + 1, totalItems - 1));
            }
            break;
        case 'ArrowUp':
            event.preventDefault();
            if (open.value) {
                handleNavigation(Math.max(activeIndex.value - 1, 0));
            }
            break;
        case 'Escape':
            event.preventDefault();
            open.value = false;
            break;
    }
};

const onOptionClick = (department, school = null) => {
    activeDepartment.value = department;
    activeSchool.value = school;
    handleSelection();
};

const scrollToActive = (index) => {
    nextTick(() => {
        const option = document.getElementById(`${idBase.value}-${index}`);
        const listbox = document.getElementById(listboxId.value);

        if (option && listbox) {
            const optionRect = option.getBoundingClientRect();
            const listboxRect = listbox.getBoundingClientRect();

            if (optionRect.bottom > listboxRect.bottom || optionRect.top < listboxRect.top) {
                option.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    });
};

const isHeaderActive = (fIndex) => {
    return activeDepartment.value && !activeSchool.value && activeDepartment.value.name === props.options[fIndex].name;
};

const isSchoolActive = (fIndex, sIndex) => {
    return activeSchool.value === props.options[fIndex].level2Departments[sIndex] && activeDepartment.value?.name === props.options[fIndex].name;
};
onMounted(() => {
    document.addEventListener('click', (event) => {
        if (comboEl.value && !comboEl.value.contains(event.target)) {
            open.value = false;
        }
    });
});

onUnmounted(() => {
    document.removeEventListener('click', () => {});
});
</script>

<style scoped lang="scss">
.combo *,
.combo *::before,
.combo *::after {
    box-sizing: border-box;
}

.combo {
    display: block;
    position: relative;
}

.combo-input {
    background-color: linear-gradient(to bottom, #fff 0%, #f5f7f9 100%);
    border: solid 1px #717171;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    font-weight: normal;
    padding: 8px 10px;
    min-width: 240px;

    &.borderRadius4 {
        border-radius: 4px;
    }

    &.padding10px {
        padding: 10px;
    }

    &[aria-disabled='true'] {
        background-color: #6c6c6c;
        border-color: #6c6c6c;
        cursor: not-allowed;
    }
}

.combo-menu {
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    display: none;
    max-height: 200px;
    overflow-y: auto;
    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;
    margin-top: 5px;
    z-index: 3;

    &.dropup {
        margin-top: 0;
        top: inherit;
        bottom: 63%;
        margin-bottom: 5px;
    }

    ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }
}

.open .combo-menu {
    display: block;
}

.combo-option {
    padding: 10px 12px 12px;
}

.combo-option:hover {
    background-color: rgb(0 0 0 / 10%);
}

.combo-option.option-current {
    outline: 3px solid #0067b8;
    outline-offset: -3px;
}

.combo-option[aria-selected='true'] {
    padding-right: 30px;
    position: relative;

    &::after {
        border-bottom: 2px solid #000;
        border-right: 2px solid #000;
        content: '';
        height: 16px;
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translate(0, -50%) rotate(45deg);
        width: 8px;
    }
}

.department-header {
    font-weight: bold;
    background-color: rgb(0 0 0 / 5%);

    &.fw-normal {
        font-weight: normal;
    }
}

.school-item {
    padding-left: 24px;
}
</style>
